/* ============================================================
   Cart drawer v3 — optimistic quantity + bulk-tier layer
   ============================================================
   The Liquid in snippets/cart-drawer.liquid is the source of
   truth: every cart change ends with the whole .drawer__inner
   re-rendered from the server (cart.js / cart-drawer.js). This
   file only makes the drawer feel instant in the window between
   a tap and that re-render:

     - +/− steppers and "+ Add N" tier buttons update the line
       total, tier strips, unit price, header count, bulk-savings
       row and subtotal immediately, then write the real
       <input name="updates[]"> and dispatch `change` so the
       normal (debounced, 300ms) cart.js sync runs.
     - The tier maths mirror the Liquid exactly: tiers come from
       data-fm-tiers=[{min,price}] (pence), highest tier with
       qty >= min wins, price floors at the server's final unit
       price when Shopify itself has a bigger discount applied.

   Everything is delegated from `document` so re-rendered markup
   needs no re-binding. No state is kept between renders — the
   DOM data attributes are the state.
   ============================================================ */

(function () {
  'use strict';

  var VAT_RATE = 1.2;

  function money(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  /* ——— VAT display mode (31-Jul-2026) ———
     fm-vat.js owns the mode (the Google Shopping `?incl_vat=true` rule,
     gclid, the manual toggle) and swaps any [data-ex-price] element's text.
     This file's optimistic qty updates rewrite the same price nodes, so
     every write here must (a) refresh BOTH data attributes — otherwise
     fm-vat's cart observer would "restore" stale old-quantity prices over
     our new ones — and (b) land in the current mode directly, so the
     shopper never sees an ex-VAT flash. */
  function vatInc() {
    return window.fmVatMode === 'inc';
  }

  function disp(pence) {
    return money(vatInc() ? Math.round(pence * 1.2) : pence);
  }

  function setPrice(el, pence, suffix) {
    if (!el) return;
    var tail = suffix || '';
    var ex = money(pence) + tail;
    var inc = money(Math.round(pence * 1.2)) + tail;
    el.setAttribute('data-ex-price', ex);
    el.setAttribute('data-inc-price', inc);
    el.textContent = vatInc() ? inc : ex;
  }

  function readTiers(card) {
    var unit = parseInt(card.getAttribute('data-fm-unit'), 10) || 0;
    try {
      var tiers = JSON.parse(card.getAttribute('data-fm-tiers') || '[]');
      if (!Array.isArray(tiers)) return [];
      /* a min-1 / 0%-off baseline row is not a real tier */
      return tiers.filter(function (t) {
        return t && t.price < unit;
      });
    } catch (e) {
      return [];
    }
  }

  /* Mirror of the Liquid tier resolution for one line at a given qty. */
  function computeLine(card, qty) {
    var unit = parseInt(card.getAttribute('data-fm-unit'), 10) || 0;
    var tiers = readTiers(card);
    var active = null;
    var next = null;
    tiers.forEach(function (t) {
      if (qty >= t.min && (!active || t.min > active.min)) active = t;
      if (t.min > qty && (!next || t.min < next.min)) next = t;
    });
    var eff = active ? active.price : unit;
    return {
      unit: unit,
      qty: qty,
      active: active,
      next: next,
      effUnit: eff,
      lineTotal: eff * qty,
      origLine: unit * qty,
      bulkSaving: active ? (unit - eff) * qty : 0
    };
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  function renderCard(card, state) {
    var q = card.querySelector('[data-fm-qty-display]');
    if (q) q.textContent = state.qty;

    var minus = card.querySelector('[data-fm-minus]');
    if (minus) minus.disabled = state.qty <= 1;

    var was = card.querySelector('[data-fm-orig-line]');
    var line = card.querySelector('[data-fm-line-total]');
    var discounted = state.lineTotal < state.origLine;
    if (was) {
      setPrice(was, state.origLine);
      setHidden(was, !discounted);
    }
    setPrice(line, state.lineTotal);

    setPrice(card.querySelector('[data-fm-unit-label]'), state.effUnit, ' each');

    var strip = card.querySelector('[data-fm-strip]');
    var stripText = card.querySelector('[data-fm-strip-text]');
    var nudge = card.querySelector('[data-fm-nudge]');
    var nudgeText = card.querySelector('[data-fm-nudge-text]');
    var nudgeBtn = card.querySelector('[data-fm-addtier-btn]');
    var upnudge = card.querySelector('[data-fm-upnudge]');
    var upnudgeText = card.querySelector('[data-fm-upnudge-text]');
    var upnudgeBtn = card.querySelector('[data-fm-upaddtier-btn]');

    if (strip) {
      setHidden(strip, !state.active);
      if (state.active && stripText) {
        stripText.textContent =
          'Bulk price applied — ' + disp(state.effUnit) +
          ' each (saving ' + disp(state.bulkSaving) + ')';
      }
    }

    var nudgeHTML = '';
    var addLabel = '';
    if (state.next) {
      var addN = state.next.min - state.qty;
      var savingAtTier = (state.unit - state.next.price) * state.next.min;
      nudgeHTML =
        '<strong>Save ' + disp(savingAtTier) + '</strong> — add ' + addN +
        ' more for ' + disp(state.next.price) + ' each';
      addLabel = '+ Add ' + addN;
    }
    if (nudge) {
      setHidden(nudge, !!state.active || !state.next);
      if (state.next && nudgeText) nudgeText.innerHTML = nudgeHTML;
      if (state.next && nudgeBtn) {
        nudgeBtn.textContent = addLabel;
        nudgeBtn.setAttribute('data-fm-addtier', state.next.min);
      }
    }
    if (upnudge) {
      setHidden(upnudge, !state.active || !state.next);
      if (state.next && upnudgeText) upnudgeText.innerHTML = nudgeHTML;
      if (state.next && upnudgeBtn) {
        upnudgeBtn.textContent = addLabel;
        upnudgeBtn.setAttribute('data-fm-addtier', state.next.min);
      }
    }
  }

  function renderTotals(drawer) {
    var cards = drawer.querySelectorAll('[data-fm-item]');
    var subtotal = 0;
    var bulkSaving = 0;
    var count = 0;
    cards.forEach(function (card) {
      var input = card.querySelector('input[data-index]');
      var qty = input ? parseInt(input.value, 10) || 0 : 0;
      var state = computeLine(card, qty);
      /* fixed-qty lines are length-priced server-side — trust the
         rendered line price, never unit × visible qty */
      var fixedLine = parseInt(card.getAttribute('data-fm-line-price'), 10);
      subtotal += isNaN(fixedLine) ? state.lineTotal : fixedLine;
      bulkSaving += state.bulkSaving;
      /* length-priced lines carry their cm count as quantity — they
         count as one item, matching the Liquid header count */
      count += card.hasAttribute('data-fm-count-as')
        ? parseInt(card.getAttribute('data-fm-count-as'), 10) || 1
        : qty;
    });

    var countEl = drawer.querySelector('[data-fm-count]');
    if (countEl) {
      var noun = count === 1
        ? countEl.getAttribute('data-fm-count-one') || 'item'
        : countEl.getAttribute('data-fm-count-many') || 'items';
      countEl.textContent = '(' + count + ' ' + noun + ')';
    }

    var savingsRow = drawer.querySelector('[data-fm-savings-row]');
    var savingsVal = drawer.querySelector('[data-fm-savings-val]');
    setHidden(savingsRow, bulkSaving <= 0);
    if (savingsVal) savingsVal.textContent = '−' + money(bulkSaving);

    var subEl = drawer.querySelector('[data-fm-subtotal]');
    var noteEl = drawer.querySelector('[data-fm-vat-note]');
    var ex = money(subtotal);
    var inc = money(Math.round(subtotal * VAT_RATE));
    var mode = window.fmVatMode === 'inc' ? 'inc' : 'ex';
    if (subEl) {
      subEl.setAttribute('data-ex-price', ex);
      subEl.setAttribute('data-inc-price', inc);
      subEl.textContent = mode === 'inc' ? inc : ex;
    }
    if (noteEl) {
      noteEl.setAttribute('data-ex-note', inc + ' inc. VAT · calculated at checkout');
      noteEl.setAttribute('data-inc-note', ex + ' ex. VAT · calculated at checkout');
      noteEl.textContent = mode === 'inc'
        ? ex + ' ex. VAT · calculated at checkout'
        : inc + ' inc. VAT · calculated at checkout';
    }
  }

  /* Apply a new quantity: optimistic paint first, then hand the real
     input to the standard cart.js pipeline (debounced fetch + full
     server re-render, which corrects any drift). */
  function applyQty(card, qty) {
    var input = card.querySelector('.fm-cartv3__qty-input');
    if (!input) return;
    qty = Math.max(1, qty);
    var max = parseInt(input.getAttribute('max'), 10);
    if (!isNaN(max) && qty > max) qty = max;

    renderCard(card, computeLine(card, qty));
    input.value = qty;

    var drawer = card.closest('cart-drawer');
    if (drawer) renderTotals(drawer);

    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', function (e) {
    var plus = e.target.closest('[data-fm-plus]');
    var minusBtn = e.target.closest('[data-fm-minus]');
    var addTier = e.target.closest('[data-fm-addtier]');
    var btn = plus || minusBtn || addTier;
    if (!btn) return;

    var card = btn.closest('[data-fm-item]');
    if (!card) return;
    var input = card.querySelector('.fm-cartv3__qty-input');
    if (!input) return;
    var current = parseInt(input.value, 10) || 1;

    if (addTier) {
      applyQty(card, parseInt(addTier.getAttribute('data-fm-addtier'), 10) || current);
    } else if (plus) {
      var step = parseInt(input.getAttribute('step'), 10) || 1;
      applyQty(card, current + step);
    } else {
      var stepDown = parseInt(input.getAttribute('step'), 10) || 1;
      if (current > 1) applyQty(card, current - stepDown);
    }
  });

  /* ——— Merged custom-line repair ———
     Two identical made-to-measure mats added without a distinguishing
     `orderID` property merge into ONE cart line of double quantity, which
     fulfilment reads as one mat. The PDP forms now stamp orderID (see
     nocustom-multioptions-form-v2.liquid), but merged lines still arrive
     from reorder flows, cart permalinks and historical carts. This module
     detects lines that are PROVABLY a merge and splits them back into one
     line per mat, each stamped with a fresh orderID so they can't re-merge.

     Detection is arithmetic-gated — when the numbers don't prove a merge,
     the line is left alone:
       - `_fm_qty_mode=unit` lines: every UI add sends quantity 1, so a
         quantity of N >= 2 is N merged mats.
       - legacy W×L lines: one mat's quantity is ceil(W×L/100) from the
         Custom Size Width/Length properties; an exact multiple is a merge.
       - legacy length lines: one mat's quantity equals the Length (cm)
         property; an exact multiple is a merge.
     Splitting never changes the amount charged — N × per-mat price both
     before and after (the cart transform prices each line identically). */

  var repairInFlight = false;
  var repairRounds = 0;
  var REPAIR_MAX_ROUNDS = 3;
  var REPAIR_MAX_MATS = 12;

  function detectMerge(item) {
    if (item.selling_plan_allocation) return null;
    var p = item.properties || {};
    var qty = item.quantity;
    if (qty < 2) return null;

    if (p['_fm_qty_mode'] === 'unit') {
      return { mats: qty, perMatQty: 1 };
    }

    var w = parseFloat(p['Custom Size Width']);
    var l = parseFloat(p['Custom Size Length']);
    if (w > 0 && l > 0) {
      var units = Math.ceil((w * l) / 100);
      if (units > 0 && qty % units === 0 && qty / units >= 2) {
        return { mats: qty / units, perMatQty: units };
      }
      return null;
    }

    var len = parseFloat(p['Length (cm)']);
    if (len > 0 && qty % len === 0 && qty / len >= 2) {
      return { mats: qty / len, perMatQty: len };
    }
    return null;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function refreshDrawer() {
    var cdi = document.querySelector('cart-drawer-items');
    if (cdi && typeof cdi.onCartUpdate === 'function') cdi.onCartUpdate();
  }

  function runMergeRepair() {
    if (repairInFlight || repairRounds >= REPAIR_MAX_ROUNDS) return;
    if (!document.querySelector('cart-drawer.fm-cartv3')) return;
    repairInFlight = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var jobs = [];
        (cart.items || []).forEach(function (item) {
          var d = detectMerge(item);
          if (d && d.mats <= REPAIR_MAX_MATS) jobs.push({ item: item, d: d });
        });
        if (!jobs.length) {
          repairInFlight = false;
          return;
        }
        repairRounds++;

        var chain = Promise.resolve();
        jobs.forEach(function (job) {
          /* shrink the merged line to one mat... */
          chain = chain.then(function () {
            return postJSON('/cart/change.js', { id: job.item.key, quantity: job.d.perMatQty });
          });
          /* ...then re-add the others as their own lines */
          for (var i = 1; i < job.d.mats; i++) {
            chain = chain.then(function () {
              var props = {};
              Object.keys(job.item.properties || {}).forEach(function (k) {
                props[k] = job.item.properties[k];
              });
              props['orderID'] = String(Math.floor(Math.random() * 999999999));
              return postJSON('/cart/add.js', {
                id: job.item.variant_id,
                quantity: job.d.perMatQty,
                properties: props
              });
            });
          }
        });

        return chain.then(refreshDrawer);
      })
      .catch(function (e) {
        /* leave the cart as-is; the merged line still displays honestly */
        console.error('[fm-cart] merge repair failed', e);
      })
      .then(function () {
        repairInFlight = false;
      });
  }

  /* Run once the page settles, and again whenever the drawer re-renders
     (every add / quantity change replaces the drawer's contents). */
  document.addEventListener('DOMContentLoaded', function () {
    setTimeout(runMergeRepair, 1200);
    var drawer = document.querySelector('cart-drawer.fm-cartv3');
    if (drawer && window.MutationObserver) {
      var t = null;
      new MutationObserver(function () {
        clearTimeout(t);
        t = setTimeout(runMergeRepair, 800);
      }).observe(drawer, { childList: true, subtree: true });
    }
  });

  /* ——— Checkout click fallback ———
     Klaviyo Hub's cart module can intercept the checkout CTA's click
     and open its own cart drawer instead (reproduced live 31-Jul-2026).
     The CSS suppresses that drawer, but a swallowed click would then
     do nothing visible — so if we're still on the page shortly after
     a CTA click, force the navigation. Stands down when normal
     navigation begins and for the cross-sell popup, which intercepts
     this click on purpose. */
  var checkoutNavStarted = false;
  window.addEventListener('beforeunload', function () {
    checkoutNavStarted = true;
  });
  window.addEventListener('pagehide', function () {
    checkoutNavStarted = true;
  });

  document.addEventListener('click', function (e) {
    var cta = e.target.closest('.cart-drawer__cta[name="checkout"]');
    if (!cta || cta.disabled) return;
    checkoutNavStarted = false;
    setTimeout(function () {
      if (checkoutNavStarted) return; /* normal navigation is under way */
      var cs = document.getElementById('cs-modal-overlay');
      if (cs && cs.hasAttribute('data-active')) return;
      if (document.visibilityState !== 'visible') return;
      window.location.href = '/checkout';
    }, 1500);
  });

  /* ——— Empty-cart escape hatch ———
     Rendered by the Liquid only when the cart holds more than 3 items
     (made-to-measure mats are one line each, so over-adding means
     tedious line-by-line removal). Two-step: "Empty cart" arms an
     inline confirm, which auto-disarms after 6s; only an explicit
     "Yes, empty it" clears. After clearing, the whole drawer is
     re-rendered from the server so the empty state (and header count)
     appear without a page reload. */

  var clearDisarmTimer = null;
  var clearInFlight = false;

  function disarmClear() {
    clearTimeout(clearDisarmTimer);
    document.querySelectorAll('[data-fm-clear]').forEach(function (b) {
      b.hidden = false;
    });
    document.querySelectorAll('[data-fm-clear-confirm]').forEach(function (c) {
      c.hidden = true;
    });
  }

  function rerenderDrawerFromServer() {
    return fetch('/?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var src = doc.querySelector('cart-drawer');
        var dst = document.querySelector('cart-drawer');
        if (src && dst) {
          var srcInner = src.querySelector('.drawer__inner');
          var dstInner = dst.querySelector('.drawer__inner');
          if (srcInner && dstInner) dstInner.innerHTML = srcInner.innerHTML;
          dst.classList.toggle('is-empty', src.classList.contains('is-empty'));
          dst.setAttribute('data-cart-items', src.getAttribute('data-cart-items') || '0');
        }
        return fetch('/?section_id=cart-icon-bubble');
      })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var bubble = document.getElementById('cart-icon-bubble');
        var srcBubble = doc.getElementById('cart-icon-bubble') || doc.body;
        if (bubble && srcBubble) bubble.innerHTML = srcBubble.innerHTML;
      });
  }

  document.addEventListener('click', function (e) {
    var arm = e.target.closest('[data-fm-clear]');
    var yes = e.target.closest('[data-fm-clear-yes]');
    var no = e.target.closest('[data-fm-clear-no]');
    if (!arm && !yes && !no) return;

    if (arm) {
      arm.hidden = true;
      var confirmEl = arm.parentElement.querySelector('[data-fm-clear-confirm]');
      if (confirmEl) confirmEl.hidden = false;
      clearTimeout(clearDisarmTimer);
      clearDisarmTimer = setTimeout(disarmClear, 6000);
      return;
    }

    if (no) {
      disarmClear();
      return;
    }

    /* yes — clear the cart */
    if (clearInFlight) return;
    clearInFlight = true;
    yes.textContent = 'Emptying…';
    fetch('/cart/clear.js', { method: 'POST' })
      .then(rerenderDrawerFromServer)
      .catch(function (err) {
        console.error('[fm-cart] empty cart failed', err);
        disarmClear();
      })
      .then(function () {
        clearInFlight = false;
      });
  });

  /* ——— Remove a whole kit ———————————————————————————————————————
     A tile kit is several cart lines rendered as one card (see
     `snippets/cart-drawer-kit.liquid`), so Remove has to take all of them.
     One `/cart/update.js` keyed by line key does it in a single request —
     removing them one at a time re-renders the drawer per component and
     shows the shopper a half-dismantled kit on the way. */

  var kitRemoveInFlight = false;

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-fm-kit-remove]');
    if (!btn) return;
    if (kitRemoveInFlight) return;

    var keys = (btn.getAttribute('data-fm-kit-keys') || '')
      .split(',')
      .map(function (k) { return k.trim(); })
      .filter(Boolean);
    if (!keys.length) return;

    kitRemoveInFlight = true;
    btn.textContent = 'Removing…';
    btn.disabled = true;

    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });

    postJSON('/cart/update.js', { updates: updates })
      .then(rerenderDrawerFromServer)
      .catch(function (err) {
        console.error('[fm-cart] kit removal failed', err);
        btn.textContent = 'Remove kit';
        btn.disabled = false;
      })
      .then(function () {
        kitRemoveInFlight = false;
      });
  });

  /* ——— Swipe-down on the drag handle closes the drawer (mobile) ——— */

  var touchStartY = null;

  document.addEventListener('touchstart', function (e) {
    var handle = e.target.closest('[data-fm-handle], .fm-cartv3__header');
    if (!handle || !e.target.closest('cart-drawer.fm-cartv3')) return;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });

  document.addEventListener('touchend', function (e) {
    if (touchStartY === null) return;
    var dy = e.changedTouches[0].clientY - touchStartY;
    touchStartY = null;
    if (dy > 80) {
      var drawer = document.querySelector('cart-drawer.fm-cartv3');
      if (drawer && typeof drawer.close === 'function') drawer.close();
    }
  }, { passive: true });
})();
